import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import * as notesActions from '../components/GridTasksNotes/actions/generalNotesActions';

type NotesContext = {
  notesList: NoteRow[],
  noteDetails: Note,
  loadingNotesList: boolean,
  updateList: (shumaID: number) => void,
  getNoteDetails: (generalNoteID: number) => void,
  resetNoteDetails: (generalNoteID: number) => void,
  createNote: (
    statusTypeID: number,
    dueDate: Date,
    responsibleUserID: number,
    freeText: string
  ) => void,
  editNote: (
    generalNoteID: number,
    statusTypeID: number,
    dueDate: Date | null,
    responsibleUserID: number,
    freeText: string
  ) => void,
  deleteNote: (generalNoteID: number) => void,
}

export type NoteRow = {
  generalNoteID: number;
  createDate: Date;
  generalNoteStatusID: string;
  generalNoteStatus: string;
  freeText: string;
  dueDate: Date | null;
  createByUserID: number,
  createUserFirstName: string,
  createUserLastName: string,
  responsibleUserID: number,
  responsibleUserFirstName: string,
  responsibleUserLastName: string
}

export type Note = {
  generalNoteID: number,
  shumaID: number,
  responsibleUserID: number,
  createDate: Date,
  statusTypeID: number;
  statusTypeName: string;
  dueDate: Date | null,
  freeText: string,
}

interface NotesGridProviderProps {
  children: ReactNode;
  shumaID: number;
}

const emptyNote = {
  generalNoteID: 0,
  shumaID: 0,
  responsibleUserID: 0,
  createDate: new Date(),
  statusTypeID: 0,
  statusTypeName: '',
  dueDate: new Date(),
  freeText: '',
};

const NotesGridContext = createContext<NotesContext>({
  notesList: [],
  noteDetails: emptyNote,
  loadingNotesList: false,
  updateList: () => {},
  getNoteDetails: () => {},
  resetNoteDetails: () => {},
  createNote: () => {},
  editNote: () => {},
  deleteNote: () => {},
});

export const useNotesGridContext = () => useContext(NotesGridContext);

export const NotesGridProvider = ({children, shumaID}: NotesGridProviderProps) => {
  const [notesList, setNotesList] = useState<NoteRow[]>([]);
  const [noteDetails, setNoteDetails] = useState<Note>(emptyNote);
  const [loadingNotesList, setLoadingNotesList] = useState(false);

  const updateList = async (shumaID: number) => {
    try {
      const notesList = await notesActions.getGeneralNotesList(shumaID);
      setNotesList([...notesList]);
    } catch (e) {
      console.error(e);
      setNotesList([]);
    }
  };

  useEffect(() => {
    if (shumaID) {
      setLoadingNotesList(true);
      updateList(shumaID).finally(() => setLoadingNotesList(false));
    }
  }, [shumaID]);

  const getNoteDetails = async (generalNoteID: number) => {
    if (generalNoteID !== -1) {
      try {
        const data = await notesActions.getGeneralNoteDetails(generalNoteID);
        setNoteDetails(data);
      } catch (e) {
        console.error(e);
        setNoteDetails(emptyNote);
      }
    }
  };

  const resetNoteDetails = (generalNoteID: number) => {
    if (generalNoteID === -1) {
      setNoteDetails(emptyNote);
    }
  };

  const createNote = async (
    statusTypeID: number,
    dueDate: Date,
    responsibleUserID: number,
    freeText: string
  ) => {
    try {
      await notesActions.addEditGeneralNote({
        generalNoteID: -1,
        shumaID,
        statusTypeID,
        dueDate,
        responsibleUserID,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };

  const editNote = async (
    generalNoteID: number,
    statusTypeID: number,
    dueDate: Date | null,
    responsibleUserID: number,
    freeText: string
  ) => {
    try {
      await notesActions.addEditGeneralNote({
        generalNoteID,
        shumaID,
        statusTypeID,
        dueDate,
        responsibleUserID,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };


  const deleteNote = async (generalNoteID: number) => {
    try {
      await notesActions.deleteGeneralNote(generalNoteID);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <NotesGridContext.Provider value={{
      notesList,
      noteDetails,
      loadingNotesList,
      updateList,
      getNoteDetails,
      resetNoteDetails,
      createNote,
      editNote,
      deleteNote,
    }}>
      {children}
    </NotesGridContext.Provider>
  );
};